import * as React from 'react';
import {
    Avatar,
    Box,
    Button,
    ButtonBase,
    Collapse,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Modal,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useLogout } from '../../service/auth.service';
import { useEffect, useMemo, useState } from 'react';
import Loader from '../components/loader';
import NotificationSnackbar from '../components/notificationSnackbar';
import {
    confirmationDialogSx,
    confirmationIconSx,
    confirmationPrimaryButtonSx,
    confirmationSecondaryButtonSx,
    confirmationTextSx,
    confirmationTitleSx,
} from '../styles/dialogStyles';
import { EK_SCORE_CONFIG_PATH } from './settings/ekScoreConfiguration/ekScoreConfiguration.paths';

const expandedDrawerWidth = 288;
const collapsedDrawerWidth = 84;
const expandedSidebarInset = 16;



const materialSymbolSx = {
    fontFamily: '"Material Symbols Rounded"',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: 24,
    lineHeight: 1,
    letterSpacing: 'normal',
    textTransform: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    wordWrap: 'normal',
    direction: 'ltr',
    fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
};

const primaryBlack = '#1C1B1F';
const activeColor = '#4F8506';
const activeBackground = '#F1F1F1';
const logoutColor = '#C40000';
const navItems = [
    { label: 'Analytics', icon: 'analytics', to: '/analytics/countryAnalysis' },
    { label: 'Initiatives', icon: 'flag', to: '/initiative' },
    { label: 'Subscriptions', icon: 'workspace_premium', to: '/subscriptionPlans' },
    { label: 'Countries', icon: 'public', to: '/countryManagement' },
    { label: 'Ecosystem Integrators', icon: 'hub', to: '/ecosystemPartners' },
];

const userManagementItems = [
    { label: 'User Persona', icon: 'badge', to: '/userType' },
    { label: 'User Onboarding', icon: 'id_card', to: '/user' },
    { label: 'Mobile Users', icon: 'smartphone', to: '/mobileUsers' },
];

const settingsItems = [
    { label: 'Crops', icon: 'agriculture', to: '/crops' },
    { label: 'App Catalogue', icon: 'apps', to: '/appCatalogue' },
    { label: 'Communications', icon: 'mail', to: '/communications' },
    { label: 'Feature Flags', icon: 'toggle_on', to: '/featureFlags' },
    { label: 'eK Link Login Page', icon: 'newspaper', to: '/loginPageCards' },
    { label: 'Redirect Configuration', icon: 'link', to: '/integratorRedirects' },
    { label: 'eK Score Configuration', icon: 'speed', to: EK_SCORE_CONFIG_PATH },
];

const footerItems = [];

function matchesNavPath(pathname, to) {
    return pathname === to || pathname.startsWith(`${to}/`);
}

// Two-letter initials, the avatar fallback when there is no photo.
function getInitials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'S';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] || '' : '';
    return (first + last).toUpperCase();
}

function MaterialSymbol({ name, sx }) {
    return (
        <Box component="span" sx={{ ...materialSymbolSx, ...sx }}>
            {name}
        </Box>
    );
}

function SidebarItem({ icon, label, to, active, inset = false, expanded, onNavigate }) {
    return (
        <ListItemButton
            component={Link}
            to={to}
            onClick={onNavigate}
            sx={{
                position: 'relative',
                zIndex: 0,
                width: expanded ? '100%' : 52,
                minWidth: expanded ? '100%' : 52,
                height: inset ? 44 : 52,
                minHeight: inset ? 44 : 52,
                pl: expanded ? (inset ? 3 : 3) : 0,
                pr: expanded ? 2 : 0,
                py: inset ? 0.75 : 1,
                borderRadius: expanded ? 0 : 1000,
                borderTopLeftRadius: expanded ? 0 : 1000,
                borderBottomLeftRadius: expanded ? 0 : 1000,
                borderTopRightRadius: 1000,
                borderBottomRightRadius: 1000,
                color: active ? activeColor : primaryBlack,
                bgcolor: 'transparent',
                justifyContent: expanded ? 'flex-start' : 'center',
                alignSelf: expanded ? 'stretch' : 'center',
                gap: expanded ? 2 : 0,
                transition: 'background-color 180ms ease, color 180ms ease, padding 220ms ease',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: expanded ? -expandedSidebarInset : 0,
                    right: expanded ? -expandedSidebarInset : 0,
                    borderRadius: expanded ? 0 : 1000,
                    borderTopLeftRadius: expanded ? 0 : 1000,
                    borderBottomLeftRadius: expanded ? 0 : 1000,
                    borderTopRightRadius: 1000,
                    borderBottomRightRadius: 1000,
                    backgroundColor: active ? activeBackground : 'transparent',
                    transition: 'background-color 180ms ease',
                    zIndex: -1,
                },
                '&:hover': {
                    bgcolor: 'transparent',
                    '&::before': {
                        backgroundColor: activeBackground,
                    },
                },
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: expanded ? 24 : 'auto',
                    mr: 0,
                    color: active ? activeColor : primaryBlack,
                    transition: 'min-width 220ms ease, color 180ms ease, transform 220ms ease',
                    transform: expanded ? 'translateX(0)' : 'translateX(0)',
                }}
            >
                <MaterialSymbol
                    name={icon}
                    sx={{
                        fontSize: inset ? 20 : 22,
                        fontVariationSettings: `"FILL" ${active ? 1 : 0}, "wght" 400, "GRAD" 0, "opsz" 24`,
                    }}
                />
            </ListItemIcon>
            <Box
                sx={{
                    flex: expanded ? '1 1 auto' : '0 0 0',
                    width: expanded ? 'auto' : 0,
                    minWidth: 0,
                    overflow: 'hidden',
                    opacity: expanded ? 1 : 0,
                    transform: expanded ? 'translateX(0)' : 'translateX(-8px)',
                    transition: 'opacity 150ms ease, transform 220ms ease, width 220ms ease',
                    whiteSpace: 'nowrap',
                }}
            >
                <ListItemText
                    sx={{ m: 0, minWidth: 0 }}
                    primary={label}
                    primaryTypographyProps={{
                        fontSize: 16,
                        lineHeight: '24px',
                        letterSpacing: '0.5px',
                        fontWeight: active ? 500 : 400,
                        noWrap: true,
                    }}
                />
            </Box>
        </ListItemButton>
    );
}

function SidebarContent({
    location,
    navigate,
    userData,
    activeCollapse,
    setActiveCollapse,
    isExpanded,
    setCollapsed,
    isMobile = false,
    onNavigate,
    handleLogout,
}) {
    const profileName =
        [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim() ||
        userData?.fullName ||
        userData?.name ||
        'Super Admin';

    // Same footer as EAG FF BIO: the avatar opens the profile, Logout sits beside it and asks first.
    const profileImage = userData?.profileImage;
    const profileInitials = getInitials(profileName);
    const isProfileSelected = matchesNavPath(location.pathname, '/userProfile');
    const [confirmLogout, setConfirmLogout] = useState(false);
    const showExpanded = isMobile ? true : isExpanded;

    // Same as EAG FF BIO: from the icon rail, one click on a group opens the sidebar and that
    // group. Never toggle it closed there — the nested list is hidden while collapsed, so a click
    // on a group icon always means "show me this menu".
    const toggleGroup = (key) => {
        if (!showExpanded) {
            setCollapsed(false);
            setActiveCollapse(key);
            return;
        }
        setActiveCollapse((prev) => (prev === key ? null : key));
    };

    const collapseToggle = (
        <IconButton
            onClick={() => setCollapsed(showExpanded)}
            aria-label={showExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-expanded={showExpanded}
            sx={{ width: 32, height: 32, flexShrink: 0, ml: showExpanded ? 'auto' : 0, color: primaryBlack }}
        >
            {showExpanded ? (
                <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 22 }} />
            ) : (
                <KeyboardDoubleArrowRightIcon sx={{ fontSize: 22 }} />
            )}
        </IconButton>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
            <Box sx={{ px: showExpanded ? 3 : 1.75, pt: isMobile ? 2 : 4, pb: 3 }}>
                {isMobile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                component="img"
                                src="/assets/logo.png"
                                alt="eKutir"
                                sx={{ width: 29, height: 33, objectFit: 'contain' }}
                            />
                            <Box>
                                <Typography sx={{ fontSize: 20, fontWeight: 700, color: primaryBlack, lineHeight: 1.2 }}>
                                    eKutir
                                </Typography>
                                <Typography sx={{ mt: 0.25, fontSize: 14, color: '#6B7280', lineHeight: 1.3 }}>
                                    Super Admin
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={onNavigate} aria-label="Close navigation" sx={{ color: primaryBlack }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                )}
                {!isMobile && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: showExpanded ? 'row' : 'column',
                            alignItems: 'center',
                            justifyContent: showExpanded ? 'flex-start' : 'center',
                            gap: showExpanded ? 1.75 : 1.5,
                        }}
                    >
                        <Box component="img" src="/assets/logo.png" alt="eKutir" sx={{ width: 29, height: 33, objectFit: 'contain' }} />
                        {showExpanded && (
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 20, fontWeight: 700, color: primaryBlack, lineHeight: 1.2 }}>
                                    eKutir
                                </Typography>
                                <Typography sx={{ mt: 0.25, fontSize: 14, color: '#6B7280', lineHeight: 1.3 }}>
                                    Super Admin
                                </Typography>
                            </Box>
                        )}
                        {collapseToggle}
                    </Box>
                )}
            </Box>

            <List sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', px: showExpanded ? 2 : 1.5, pt: 0.5, pb: 1 }}>
                {navItems.map((item) => (
                    <SidebarItem
                        key={item.label}
                        {...item}
                        active={location.pathname === item.to}
                        expanded={showExpanded}
                        onNavigate={() => {
                            setActiveCollapse(null);
                            onNavigate?.();
                        }}
                    />
                ))}
            </List>

            <List sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', px: showExpanded ? 2 : 1.5, py: 0 }}>
                <ListItemButton
                    onClick={() => toggleGroup('user')}
                    sx={{
                        position: 'relative',
                        zIndex: 0,
                        width: showExpanded ? '100%' : 52,
                        minWidth: showExpanded ? '100%' : 52,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        height: 52,
                        minHeight: 52,
                        pl: showExpanded ? 3 : 0,
                        pr: showExpanded ? 1.25 : 0,
                        py: 1,
                        borderRadius: showExpanded ? 0 : 1000,
                        borderTopLeftRadius: showExpanded ? 0 : 1000,
                        borderBottomLeftRadius: showExpanded ? 0 : 1000,
                        borderTopRightRadius: 1000,
                        borderBottomRightRadius: 1000,
                        color: primaryBlack,
                        bgcolor: 'transparent',
                        justifyContent: showExpanded ? 'flex-start' : 'center',
                        alignSelf: showExpanded ? 'stretch' : 'center',
                        gap: showExpanded ? 2 : 0,
                        transition: 'background-color 180ms ease, color 180ms ease, padding 220ms ease',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            borderRadius: showExpanded ? 0 : 1000,
                            borderTopLeftRadius: showExpanded ? 0 : 1000,
                            borderBottomLeftRadius: showExpanded ? 0 : 1000,
                            borderTopRightRadius: 1000,
                            borderBottomRightRadius: 1000,
                            backgroundColor: 'transparent',
                            transition: 'background-color 180ms ease',
                            zIndex: -1,
                        },
                        '&:hover': {
                            bgcolor: 'transparent',
                            '&::before': {
                                backgroundColor: activeBackground,
                            },
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: showExpanded ? 24 : 'auto',
                            color: primaryBlack,
                            transition: 'min-width 220ms ease, color 180ms ease',
                        }}
                    >
                        <MaterialSymbol
                            name="person"
                            sx={{
                                fontSize: 22,
                                fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
                            }}
                        />
                    </ListItemIcon>
                    <Box
                        sx={{
                            flex: showExpanded ? '1 1 auto' : '0 0 0',
                            width: showExpanded ? 'auto' : 0,
                            minWidth: 0,
                            overflow: 'hidden',
                            opacity: showExpanded ? 1 : 0,
                            transform: showExpanded ? 'translateX(0)' : 'translateX(-8px)',
                            transition: 'opacity 150ms ease, transform 220ms ease, width 220ms ease',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <ListItemText
                            sx={{ m: 0, minWidth: 0 }}
                            primary="User Management"
                            primaryTypographyProps={{
                                fontSize: 16,
                                lineHeight: '24px',
                                letterSpacing: '0.5px',
                                fontWeight: 400,
                                noWrap: true,
                            }}
                        />
                    </Box>
                    {activeCollapse === 'user' ? (
                        showExpanded ? (
                            <ExpandLessIcon sx={{ color: primaryBlack, ml: 'auto', flexShrink: 0, mr: 0 }} />
                        ) : (
                            <ExpandLessIcon sx={{ color: primaryBlack, fontSize: 20, ml: 0.5 }} />
                        )
                    ) : (
                        <ExpandMoreIcon sx={{ color: primaryBlack, fontSize: 20, ml: showExpanded ? 'auto' : 0.5, flexShrink: 0, mr: 0 }} />
                    )}
                </ListItemButton>

                <Collapse in={showExpanded && activeCollapse === 'user'} timeout="auto" unmountOnExit>
                    <List sx={{ pt: 0.5 }}>
                        {userManagementItems.map((item) => (
                            <SidebarItem
                                key={item.label}
                                {...item}
                                active={matchesNavPath(location.pathname, item.to)}
                                expanded={showExpanded}
                                inset
                                onNavigate={onNavigate}
                            />
                        ))}
                    </List>
                </Collapse>
            </List>

            <List sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', px: showExpanded ? 2 : 1.5, pt: 0.5, pb: 0 }}>
                <ListItemButton
                    onClick={() => toggleGroup('settings')}
                    sx={{
                        position: 'relative',
                        zIndex: 0,
                        width: showExpanded ? '100%' : 52,
                        minWidth: showExpanded ? '100%' : 52,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        height: 52,
                        minHeight: 52,
                        pl: showExpanded ? 3 : 0,
                        pr: showExpanded ? 1.25 : 0,
                        py: 1,
                        borderRadius: showExpanded ? 0 : 1000,
                        borderTopLeftRadius: showExpanded ? 0 : 1000,
                        borderBottomLeftRadius: showExpanded ? 0 : 1000,
                        borderTopRightRadius: 1000,
                        borderBottomRightRadius: 1000,
                        color: primaryBlack,
                        bgcolor: 'transparent',
                        justifyContent: showExpanded ? 'flex-start' : 'center',
                        alignSelf: showExpanded ? 'stretch' : 'center',
                        gap: showExpanded ? 2 : 0,
                        transition: 'background-color 180ms ease, color 180ms ease, padding 220ms ease',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            borderRadius: showExpanded ? 0 : 1000,
                            borderTopLeftRadius: showExpanded ? 0 : 1000,
                            borderBottomLeftRadius: showExpanded ? 0 : 1000,
                            borderTopRightRadius: 1000,
                            borderBottomRightRadius: 1000,
                            backgroundColor: 'transparent',
                            transition: 'background-color 180ms ease',
                            zIndex: -1,
                        },
                        '&:hover': {
                            bgcolor: 'transparent',
                            '&::before': {
                                backgroundColor: activeBackground,
                            },
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: showExpanded ? 24 : 'auto',
                            color: primaryBlack,
                            transition: 'min-width 220ms ease, color 180ms ease',
                        }}
                    >
                        <MaterialSymbol
                            name="settings"
                            sx={{
                                fontSize: 22,
                                fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
                            }}
                        />
                    </ListItemIcon>
                    <Box
                        sx={{
                            flex: showExpanded ? '1 1 auto' : '0 0 0',
                            width: showExpanded ? 'auto' : 0,
                            minWidth: 0,
                            overflow: 'hidden',
                            opacity: showExpanded ? 1 : 0,
                            transform: showExpanded ? 'translateX(0)' : 'translateX(-8px)',
                            transition: 'opacity 150ms ease, transform 220ms ease, width 220ms ease',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <ListItemText
                            sx={{ m: 0, minWidth: 0 }}
                            primary="Configuration"
                            primaryTypographyProps={{
                                fontSize: 16,
                                lineHeight: '24px',
                                letterSpacing: '0.5px',
                                fontWeight: 400,
                                noWrap: true,
                            }}
                        />
                    </Box>
                    {activeCollapse === 'settings' ? (
                        showExpanded ? (
                            <ExpandLessIcon sx={{ color: primaryBlack, ml: 'auto', flexShrink: 0, mr: 0 }} />
                        ) : (
                            <ExpandLessIcon sx={{ color: primaryBlack, fontSize: 20, ml: 0.5 }} />
                        )
                    ) : (
                        <ExpandMoreIcon sx={{ color: primaryBlack, fontSize: 20, ml: showExpanded ? 'auto' : 0.5, flexShrink: 0, mr: 0 }} />
                    )}
                </ListItemButton>

                <Collapse in={showExpanded && activeCollapse === 'settings'} timeout="auto" unmountOnExit>
                    <List sx={{ pt: 0.5, pb: 2 }}>
                        {settingsItems.map((item) => (
                            <SidebarItem
                                key={item.label}
                                {...item}
                                active={matchesNavPath(location.pathname, item.to)}
                                expanded={showExpanded}
                                inset
                                onNavigate={onNavigate}
                            />
                        ))}
                    </List>
                </Collapse>
            </List>

            <Box
                sx={{
                    mt: 'auto',
                    display: 'flex',
                    flexDirection: showExpanded ? 'row' : 'column',
                    alignItems: 'center',
                    justifyContent: showExpanded ? 'space-between' : 'center',
                    gap: showExpanded ? 1.5 : 1,
                    px: showExpanded ? 3 : 1.5,
                    py: 1.5,
                    borderTop: '1px solid #EEF2F7',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <IconButton
                        onClick={() => {
                            navigate('/userProfile');
                            onNavigate?.();
                        }}
                        aria-label="Open user profile"
                        aria-current={isProfileSelected ? 'page' : undefined}
                        sx={{
                            p: 0,
                            flexShrink: 0,
                            borderRadius: '50%',
                            outline: isProfileSelected ? `2px solid ${activeColor}` : 'none',
                            outlineOffset: 2,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: profileImage ? 'transparent' : activeColor,
                                color: '#FFFFFF',
                                fontSize: 13,
                                fontWeight: 600,
                            }}
                            src={profileImage || undefined}
                            alt={profileName}
                        >
                            {!profileImage ? profileInitials : null}
                        </Avatar>
                    </IconButton>
                    {showExpanded && (
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: 14, fontWeight: 500, lineHeight: '20px', color: primaryBlack }}>
                                {profileName}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 12, lineHeight: '16px', color: '#6B7280' }}>
                                Super Admin
                            </Typography>
                        </Box>
                    )}
                </Box>
                <ButtonBase
                    onClick={() => setConfirmLogout(true)}
                    aria-label="Logout"
                    sx={{
                        flexShrink: 0,
                        gap: 1,
                        height: showExpanded ? 36 : 32,
                        width: showExpanded ? 'auto' : 32,
                        px: showExpanded ? 1 : 0,
                        borderRadius: showExpanded ? '8px' : '50%',
                        color: logoutColor,
                        fontSize: 13,
                        fontWeight: 500,
                        '&:hover': { bgcolor: '#FFF7F6' },
                    }}
                >
                    <LogoutIcon sx={{ fontSize: 18 }} />
                    {showExpanded && <span>Logout</span>}
                </ButtonBase>
            </Box>

            <Modal open={confirmLogout} onClose={() => setConfirmLogout(false)}>
                <Box sx={confirmationDialogSx}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
                        <Box sx={confirmationIconSx}>
                            <LogoutIcon sx={{ fontSize: 36, color: logoutColor }} />
                        </Box>
                        <Typography sx={confirmationTitleSx}>Log out?</Typography>
                        <Typography sx={confirmationTextSx}>
                            You will need to sign in again to use eKutir Super Admin.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, pt: 1 }}>
                            <Button
                                variant="contained"
                                data-testid="confirm-logout"
                                onClick={() => {
                                    setConfirmLogout(false);
                                    handleLogout();
                                    onNavigate?.();
                                }}
                                sx={confirmationPrimaryButtonSx}
                            >
                                Log out
                            </Button>
                            <Button onClick={() => setConfirmLogout(false)} sx={confirmationSecondaryButtonSx}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

export default function MiniDrawer() {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { logout } = useLogout();
    const [loading, setLoading] = useState(false);
    const [activeCollapse, setActiveCollapse] = useState('user');
    const [userData, setUserData] = useState(null);
    // Starts on the icon rail, like EAG FF BIO; the header toggle (or a click on a group) opens it.
    const [collapsed, setCollapsed] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [alert, setAlert] = useState('');
    const [openAlert, setOpenAlert] = useState(false);
    const alertClose = () => setAlert('');
    const [responseMsg, setResponseMsg] = useState('');

    const ALERT_DURATION = 2000;

    const isUserManagementActive = useMemo(
        () => userManagementItems.some((item) => matchesNavPath(location.pathname, item.to)),
        [location.pathname]
    );

    const isSettingsActive = useMemo(
        () => settingsItems.some((item) => matchesNavPath(location.pathname, item.to)),
        [location.pathname]
    );

    useEffect(() => {
        const syncUserData = () => {
            const currentUserData = localStorage.getItem('userData');
            if (currentUserData) {
                setUserData(JSON.parse(currentUserData));
            }
        };

        syncUserData();
        window.addEventListener('user-data-updated', syncUserData);
        window.addEventListener('storage', syncUserData);

        return () => {
            window.removeEventListener('user-data-updated', syncUserData);
            window.removeEventListener('storage', syncUserData);
        };
    }, []);

    useEffect(() => {
        if (isUserManagementActive) {
            setActiveCollapse('user');
        }
    }, [isUserManagementActive]);

    useEffect(() => {
        if (isSettingsActive) {
            setActiveCollapse('settings');
        }
    }, [isSettingsActive]);

    useEffect(() => {
        if (isMobile) {
            setMobileOpen(false);
        }
    }, [location.pathname, isMobile]);

    const handleLogout = () => {
        setLoading(true);
        logout()
            .then(() => {
                navigate('/auth/login');
                setResponseMsg("Logout successful");
                setAlert('true');
                setOpenAlert(true);
                setTimeout(() => {
                    alertClose();
                    navigate('/auth/login');
                    setLoading(false);
                }, ALERT_DURATION);

            })
            .catch((err) => {
                setResponseMsg("Logout failed");
                setAlert('false');
                setOpenAlert(true);
                setTimeout(() => {
                    alertClose();
                    navigate('/auth/login');
                    setLoading(false);
                }, ALERT_DURATION);

            });
    };

    const isExpanded = !collapsed;
    const closeMobileDrawer = () => setMobileOpen(false);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden', bgcolor: '#FEFEFE' }}>
            <CssBaseline />

            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: isExpanded ? expandedDrawerWidth : collapsedDrawerWidth,
                        flexShrink: 0,
                        overflow: 'hidden',
                        transition: 'width 240ms cubic-bezier(0.2, 0, 0, 1)',
                        '& .MuiDrawer-docked': {
                            overflow: 'hidden',
                            transition: 'width 240ms cubic-bezier(0.2, 0, 0, 1)',
                        },
                        '& .MuiDrawer-paper': {
                            width: isExpanded ? expandedDrawerWidth : collapsedDrawerWidth,
                            boxSizing: 'border-box',
                            borderRight: '1px solid #E7ECF2',
                            backgroundColor: '#FFFFFF',
                            overflowX: 'hidden',
                            overflowY: 'hidden',
                            transition: 'width 240ms cubic-bezier(0.2, 0, 0, 1), box-shadow 180ms ease',
                            boxShadow: isExpanded ? '0px 8px 24px rgba(28, 27, 31, 0.04)' : 'none',
                            willChange: 'width',
                        },
                    }}
                >
                    <SidebarContent
                        location={location}
                        navigate={navigate}
                        userData={userData}
                        activeCollapse={activeCollapse}
                        setActiveCollapse={setActiveCollapse}
                        isExpanded={isExpanded}
                        setCollapsed={setCollapsed}
                        handleLogout={handleLogout}
                    />
                </Drawer>
            )}

            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={closeMobileDrawer}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            width: '100%',
                            maxWidth: '100%',
                            height: '100dvh',
                            boxSizing: 'border-box',
                            borderRight: 'none',
                            backgroundColor: '#FFFFFF',
                            overflowX: 'hidden',
                        },
                    }}
                >
                    <SidebarContent
                        location={location}
                        navigate={navigate}
                        userData={userData}
                        activeCollapse={activeCollapse}
                        setActiveCollapse={setActiveCollapse}
                        isExpanded
                        setCollapsed={setCollapsed}
                        isMobile
                        onNavigate={closeMobileDrawer}
                        handleLogout={handleLogout}
                    />
                </Drawer>
            )}

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    flexBasis: 0,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    p: { xs: 2, sm: 3, md: 4 },
                    bgcolor: '#FEFEFE',
                    transition: 'width 240ms cubic-bezier(0.2, 0, 0, 1)',
                    '& > *': {
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                    },
                }}
            >
                {isMobile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open navigation"
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                border: '1px solid #E7ECF2',
                                color: primaryBlack,
                                backgroundColor: '#FFFFFF',
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                )}
                <Outlet />
                {loading && <Loader />}


                <NotificationSnackbar
                    open={openAlert && !!alert}
                    onClose={alertClose}
                    message={responseMsg}
                    severity={alert === 'true' ? 'success' : 'error'}
                    messageTestId={alert === 'true' ? 'alert-success' : 'alert-error'}
                />
            </Box>
        </Box>
    );
}
